/**
 * 세션이 «왜» 끝났는지 — 로그인 화면이 한 줄로 알려 주도록 잠깐 남겨 둔다 (2026-09-15).
 *
 * 서버 PR #1365 부터 비밀번호 변경 · 모든 기기 로그아웃 · 관리자 비활성/비밀번호 초기화 직후
 * 쥐고 있던 액세스 토큰이 즉시 `401 TOKEN_REVOKED` 가 된다. 인터셉터가 세션을 버리면
 * AuthGuard 가 `/login` 으로 «화면만» 옮기는데(`location.state` 는 `from` 이 차지한다),
 * 그러면 사람은 이유 없이 로그인 화면을 만난다 — 그래서 이유를 여기 남기고 로그인 화면이 읽는다.
 *
 * ★ sessionStorage — 이 탭에서만 산다. 다른 탭은 제 요청이 401 을 받을 때 제 이유를 남긴다.
 * ★ 유효 10분 — 한참 뒤 평범한 로그아웃에서 옛 안내가 뜨지 않게.
 * ★ 이미 남은 이유를 덮지 않는 것이 기본이다 — 비밀번호를 바꾼 탭에는 뒤따르는 폐기 401 보다
 *   「비밀번호가 바뀌었다」 가 더 정확하다.
 * ★ 서버는 폐기 사유를 가르지 않는다(모두 `TOKEN_REVOKED`). 그래서 이유는 둘뿐이다 —
 *   이 탭이 직접 안 것(`password-changed`)과 서버가 알려 준 것(`revoked`).
 */
export type SessionEndReason = 'revoked' | 'password-changed';

const KEY = 'workchat:session-end-reason';
const REASONS: readonly SessionEndReason[] = ['revoked', 'password-changed'];

export const SESSION_END_REASON_TTL_MS = 10 * 60 * 1000;

interface Stored { reason: SessionEndReason; at: number }

function store(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null; // 사이트 데이터 차단 등 — 안내만 못 할 뿐 로그인은 된다.
  }
}

function read(s: Storage, now: number): Stored | null {
  try {
    const raw = s.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<Stored>;
    const ok = typeof v.at === 'number' && REASONS.includes(v.reason as SessionEndReason);
    if (!ok || now - (v.at as number) > SESSION_END_REASON_TTL_MS) {
      s.removeItem(KEY);
      return null;
    }
    return v as Stored;
  } catch {
    return null;
  }
}

/** 이유를 남긴다. 기본은 이미 남은 것을 덮지 않는다(`overwrite` 로 덮는다). */
export function markSessionEndReason(
  reason: SessionEndReason,
  opts: { overwrite?: boolean } = {},
  now: number = Date.now(),
): void {
  const s = store();
  if (!s) return;
  try {
    if (!opts.overwrite && read(s, now)) return;
    s.setItem(KEY, JSON.stringify({ reason, at: now } satisfies Stored));
  } catch {
    /* 저장 실패는 조용히 — 안내 한 줄 때문에 로그아웃 흐름을 깨지 않는다. */
  }
}

/** 남은 이유를 읽는다(지우지 않는다). 없거나 10분이 지났으면 null. */
export function peekSessionEndReason(now: number = Date.now()): SessionEndReason | null {
  const s = store();
  return s ? read(s, now)?.reason ?? null : null;
}

/** 이유를 지운다 — 로그인 화면이 보여 준 뒤 부른다. */
export function clearSessionEndReason(): void {
  try {
    store()?.removeItem(KEY);
  } catch {
    /* noop */
  }
}
